/**
 * Follower Repository
 * Extends BaseRepository for shop followers
 */

import { Shop } from '../domain/types';
import { COLLECTIONS } from '../domain/collections';
import {
    BaseRepository,
    BaseEntity
} from '../domain/services/BaseRepository';
import {
    collection,
    query,
    where,
    limit as firestoreLimit,
    getDoc,
    getDocs,
    doc as firestoreDoc,
    setDoc,
    deleteDoc
} from 'firebase/firestore';
import { db } from '../infrastructure/firebaseConfig';
import { logger } from '../infrastructure/logger';
import { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';

export interface FollowerEntity extends BaseEntity {
    shopId: string;
    userId: string;
    createdAt: string;
    updatedAt?: string;
}

class FollowerRepository extends BaseRepository<FollowerEntity> {
    protected collectionName = COLLECTIONS.SHOP_FOLLOWERS;
    private shopsColl = COLLECTIONS.SHOPS;

    protected toEntity(doc: QueryDocumentSnapshot<DocumentData>): FollowerEntity {
        const data = doc.data();
        return {
            id: doc.id,
            shopId: (data.shopId as string) || '',
            userId: (data.userId as string) || '',
            createdAt: (data.createdAt as string) || new Date().toISOString(),
            updatedAt: (data.updatedAt as string) || new Date().toISOString()
        };
    }

    /**
     * Follow a shop
     */
    async followShop(shopId: string, userId: string): Promise<void> {
        const followerId = `${shopId}_${userId}`;
        await setDoc(firestoreDoc(db, this.collectionName, followerId), {
            shopId,
            userId
        });
    }

    /**
     * Unfollow a shop
     */
    async unfollowShop(shopId: string, userId: string): Promise<void> {
        const followerId = `${shopId}_${userId}`;
        await this.delete(followerId);
    }

    /**
     * Check if user follows shop
     */
    async isFollowingShop(shopId: string, userId: string): Promise<boolean> {
        const followers = await this.findByField('shopId', shopId);
        return followers.some(f => f.userId === userId);
    }

    /**
     * Get followers count for a shop
     */
    async getShopFollowersCount(shopId: string): Promise<number> {
        const followers = await this.findByField('shopId', shopId);
        return followers.length;
    }

    /**
     * Get shops followed by user (with shop details)
     */
    async getShopsFollowingByUser(userId: string): Promise<Shop[]> {
        try {
            const q = query(
                this.collection,
                firestoreLimit(100)
            );
            const snapshot = await getDocs(q);

            const shopIds = snapshot.docs
                .map(doc => this.toEntity(doc))
                .filter(f => f.userId === userId)
                .map(f => f.shopId);

            if (shopIds.length === 0) return [];

            // Fetch shop details using direct Firestore queries
            const shopPromises = shopIds.map(async (shopId: string) => {
                const shopDoc = await getDoc(firestoreDoc(db, this.shopsColl, shopId));
                if (shopDoc.exists()) {
                    const data = shopDoc.data();
                    return {
                        id: shopDoc.id,
                        ...data,
                        stats: {
                            totalSales: 0,
                            totalRevenue: 0,
                            totalProducts: 0,
                            totalServices: 0,
                            rating: 0,
                            reviewCount: 0,
                            followers: 0,
                            totalViews: 0,
                            ...(data.stats || {})
                        }
                    } as Shop;
                }
                return null;
            });

            const shops = await Promise.all(shopPromises);
            return shops.filter((s): s is Shop => s !== null && s.status === 'active');
        } catch (error) {
            logger.error(`[FollowerRepository] Error getting shops followed by user ${userId}`, error);
            return [];
        }
    }

    /**
     * Get shop IDs followed by user
     */
    async getFollowingShopIds(userId: string): Promise<string[]> {
        try {
            const q = query(
                this.collection,
                firestoreLimit(100)
            );
            const snapshot = await getDocs(q);
            return snapshot.docs
                .map(doc => this.toEntity(doc))
                .filter(f => f.userId === userId)
                .map(f => f.shopId);
        } catch (error) {
            logger.error(`[FollowerRepository] Error getting following shop IDs for user ${userId}`, error);
            return [];
        }
    }
}

export const followerRepository = new FollowerRepository();
